import { Card, CardContent } from '@/components/ui/card';
import {
  Zap,
  TrendingUp,
  DollarSign,
  Clock,
  Shield,
  Smartphone,
  Users,
  BarChart3,
  CheckCircle,
  Star,
} from 'lucide-react';

const Benefits = () => {
  const mainBenefits = [
    {
      icon: <Zap className="w-12 h-12 text-red-600" />,
      title: 'Automation & Efficiency',
      description: 'Reduce manual work by up to 80% with intelligent automation',
      points: [
        'Automated booking and dispatching',
        'Intelligent route optimization',
        'Auto-generated invoices and reports',
        'Automated notifications and alerts',
        'Smart vendor assignment',
      ],
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-red-600" />,
      title: 'Accelerated Growth',
      description: 'Scale your operations without proportional increase in costs',
      points: [
        'Handle 5x more bookings with same team',
        'Expand to new markets effortlessly',
        'Multi-location management',
        'Unlimited user scaling',
        'Enterprise-ready infrastructure',
      ],
    },
    {
      icon: <DollarSign className="w-12 h-12 text-red-600" />,
      title: 'Cost Reduction',
      description: 'Significant savings across operations and resources',
      points: [
        'Reduce operational costs by 40-60%',
        'Eliminate manual data entry errors',
        'Lower customer acquisition costs',
        'Optimize fleet utilization',
        'Reduce phone and support costs',
      ],
    },
    {
      icon: <Shield className="w-12 h-12 text-red-600" />,
      title: 'Enhanced Trust & Transparency',
      description: 'Build stronger relationships with complete visibility',
      points: [
        'Real-time tracking for customers',
        '99.9% error-free billing',
        'Complete audit trails',
        'Digital documentation',
        'Transparent pricing models',
      ],
    },
    {
      icon: <Smartphone className="w-12 h-12 text-red-600" />,
      title: 'Brand Control',
      description: 'Own your digital presence with white-labeled solutions',
      points: [
        'Custom branded iOS & Android apps',
        'Your logo, colors, and identity',
        'Direct customer relationships',
        'No third-party intermediaries',
        'Complete platform control',
      ],
    },
    {
      icon: <Clock className="w-12 h-12 text-red-600" />,
      title: '24/7 Availability',
      description: 'Always-on platform for customers and partners',
      points: [
        'Round-the-clock booking capability',
        '99.9% platform uptime',
        'Global accessibility',
        'Mobile and web access',
        'Offline mode support',
      ],
    },
  ];

  const businessImpact = [
    {
      metric: '80%',
      label: 'Reduction in Manual Work',
      icon: <Zap className="w-8 h-8 text-red-600" />,
    },
    {
      metric: '5x',
      label: 'Increase in Booking Capacity',
      icon: <TrendingUp className="w-8 h-8 text-red-600" />,
    },
    {
      metric: '60%',
      label: 'Lower Operational Costs',
      icon: <DollarSign className="w-8 h-8 text-red-600" />,
    },
    {
      metric: '99.9%',
      label: 'Error-Free Processing',
      icon: <Shield className="w-8 h-8 text-red-600" />,
    },
  ];

  const industries = [
    {
      name: 'Corporate Travel Agencies',
      benefits: [
        'Streamlined corporate booking management',
        'Department-wise expense tracking',
        'Policy compliance automation',
        'Comprehensive travel analytics',
      ],
    },
    {
      name: 'Transportation Service Providers',
      benefits: [
        'Efficient fleet management',
        'Real-time driver coordination',
        'Route optimization',
        'Revenue maximization',
      ],
    },
    {
      name: 'Travel Management Companies',
      benefits: [
        'Multi-client management',
        'Vendor network optimization',
        'Automated commission handling',
        'White-labeled client portals',
      ],
    },
    {
      name: 'Enterprise In-house Travel Teams',
      benefits: [
        'Centralized travel management',
        'Budget control and allocation',
        'Employee self-service booking',
        'Compliance and reporting',
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-white py-20 px-4" data-testid="benefits-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Why Choose Frogleap Technologies</h1>
          <p className="text-xl text-gray-600">
            Transform your travel operations with measurable benefits in automation, growth, and efficiency.
          </p>
        </div>
      </section>

      {/* Business Impact Metrics */}
      <section className="py-16 bg-white" data-testid="impact-metrics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Proven Business Impact</h2>
            <p className="text-xl text-gray-600">Real results that our partners experience</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessImpact.map((item, index) => (
              <div key={index} className="text-center" data-testid={`impact-${index}`}>
                <div className="flex justify-center mb-4">{item.icon}</div>
                <div className="text-4xl font-bold text-red-600 mb-2">{item.metric}</div>
                <div className="text-gray-600 font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Benefits */}
      <section className="py-16 bg-gray-50" data-testid="main-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Benefits</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every feature is designed to deliver tangible benefits to your business operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainBenefits.map((benefit, index) => (
              <Card key={index} className="border-none shadow-lg" data-testid={`benefit-card-${index}`}>
                <CardContent className="p-8">
                  <div className="mb-6">{benefit.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 mb-6">{benefit.description}</p>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-Specific Benefits */}
      <section className="py-16 bg-white" data-testid="industry-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits by Industry</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored solutions that address specific needs across different travel industry segments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="border-none shadow-md" data-testid={`industry-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mt-1">{industry.name}</h3>
                  </div>
                  <ul className="space-y-2 ml-14">
                    {industry.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-red-600 mt-1">•</span>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 bg-gray-50" data-testid="roi-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">Return on Investment</h2>
              <p className="text-xl text-red-100 mb-8 text-center">
                Most partners see positive ROI within 3-6 months of implementation
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold mb-2">3-6 Months</h3>
                  <p className="text-red-100">Average time to positive ROI</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                  <DollarSign className="w-10 h-10 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold mb-2">40-60%</h3>
                  <p className="text-red-100">Reduction in operational costs</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                  <TrendingUp className="w-10 h-10 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold mb-2">300%+</h3>
                  <p className="text-red-100">Average 3-year ROI</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Placeholder */}
      <section className="py-16 bg-white" data-testid="testimonial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Partners Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((index) => (
              <Card key={index} className="border-none shadow-md" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex space-x-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-5 h-5 fill-red-600 text-red-600" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "Frogleap Technologies has completely transformed our operations. We're handling 5x more bookings
                    with the same team size. The automation features are a game-changer."
                  </p>
                  <div>
                    <p className="font-semibold text-gray-900">Partner Name</p>
                    <p className="text-sm text-gray-600">CEO, Travel Company</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700 text-white" data-testid="benefits-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience These Benefits?</h2>
          <p className="text-xl mb-8 text-red-100">
            Let's discuss how Frogleap Technologies can deliver measurable results for your business.
          </p>
          <a href="/contact">
            <button className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors" data-testid="benefits-contact-btn">
              Get Started Today
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Benefits;